import React, { useMemo, useRef, useState } from 'react'

import type { HouseTreeNode, ProductGroup } from '../types/catalog'
import { flattenHouseLeaves, houseLeafLabelMap } from '../utils/companyMappers'
import { DeviceAssignmentField } from './DeviceAssignmentField'
import { HouseAssignmentField } from './HouseAssignmentField'
import {
  badgeForSub,
  devicePlaceholdersFor,
} from './EditDealModal/helpers'
import type { LineItemDraft } from './EditDealModal/types'

interface LineItemEditDraft {
  name: string
  desc: string
  qty: number
}

interface LineItemEditorProps {
  item: LineItemDraft
  productGroups: ProductGroup[]
  houseTree: HouseTreeNode[]
  isDropdownOpen: boolean
  onDropdownOpenChange: (open: boolean) => void
  onUpdate: (next: LineItemDraft) => void
  onDevicesChange: (nextDevices: string[], nextCatalog: string[]) => void
  onRemove: () => void
}

export const LineItemEditor: React.FunctionComponent<LineItemEditorProps> = (props) => {
  const {
    item,
    houseTree,
    isDropdownOpen,
    onDropdownOpenChange,
    onUpdate,
    onDevicesChange,
    onRemove,
  } = props

  const [edit, setEdit] = useState<LineItemEditDraft | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const editTitleInputRef = useRef<HTMLInputElement | null>(null)

  const badge = badgeForSub(item.type)
  const placeholders = devicePlaceholdersFor(item.type)
  const swHouseLeaves = useMemo(() => flattenHouseLeaves(houseTree), [houseTree])
  const swCatalog = useMemo(
    () => Array.from(new Set([...item.devices, ...swHouseLeaves])),
    [item.devices, swHouseLeaves],
  )
  const swLeafLabels = useMemo(() => houseLeafLabelMap(houseTree), [houseTree])
  const displayDevice = (device: string): string =>
    item.type === 'sw' ? swLeafLabels[device] ?? device : device

  function startEdit(): void {
    setConfirmDelete(false)
    setEdit({ name: item.name, desc: item.desc ?? '', qty: item.qty })
    setTimeout(() => {
      editTitleInputRef.current?.focus()
      editTitleInputRef.current?.select()
    }, 10)
  }

  function commitEdit(): void {
    if (!edit) return
    onUpdate({
      ...item,
      name: edit.name.trim() || item.name,
      desc: edit.desc.trim() || undefined,
      qty: Math.max(1, edit.qty),
    })
    setEdit(null)
  }

  function cancelEdit(): void {
    setEdit(null)
  }

  function askDelete(): void {
    if (confirmDelete) return
    cancelEdit()
    setConfirmDelete(true)
  }

  function dismissDelete(): void {
    setConfirmDelete(false)
  }

  return (
    <div className={`li-card${edit ? ' li-editing' : ''}`}>
      <div className="li-card-header">
        <div className="li-card-info">
          <div className="li-card-title">
            {edit ? (
              <>
                <input
                  ref={editTitleInputRef}
                  className="li-edit-title"
                  value={edit.name}
                  onChange={(event) =>
                    setEdit((current) =>
                      current ? { ...current, name: event.target.value } : current,
                    )
                  }
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    border: '1px solid #0071e3',
                    borderRadius: 6,
                    padding: '4px 8px',
                    height: 30,
                    width: 240,
                    fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />{' '}
                <span className={`badge ${badge.className}`}>{item.badgeLabel}</span>
              </>
            ) : (
              <>
                {item.name}{' '}
                <span className={`badge ${badge.className}`}>{item.badgeLabel}</span>
              </>
            )}
          </div>
          {edit && item.desc ? (
            <div className="li-card-desc">
              <input
                className="li-edit-desc"
                value={edit.desc}
                placeholder="Description"
                onChange={(event) =>
                  setEdit((current) =>
                    current ? { ...current, desc: event.target.value } : current,
                  )
                }
                style={{
                  fontSize: 12,
                  color: '#6e6e73',
                  border: '1px solid #e5e5ea',
                  borderRadius: 6,
                  padding: '3px 8px',
                  height: 26,
                  width: '100%',
                  maxWidth: 360,
                  fontFamily: 'inherit',
                  outline: 'none',
                  marginTop: 4,
                }}
              />
            </div>
          ) : (
            <>
              {item.desc && !edit && <div className="li-card-desc">{item.desc}</div>}
              {item.memo && !edit && (
                <div style={{ fontSize: 12, color: 'var(--secondary)', marginTop: 4 }}>
                  Memo: {item.memo}
                </div>
              )}
            </>
          )}
        </div>
        <div className="li-card-meta">
          {edit ? (
            <>
              <span style={{ fontSize: 13, color: '#6e6e73' }}>Qty:</span>
              <input
                type="number"
                className="li-edit-qty"
                min={1}
                value={edit.qty}
                onChange={(event) =>
                  setEdit((current) =>
                    current
                      ? { ...current, qty: Math.max(1, Number(event.target.value) || 1) }
                      : current,
                  )
                }
                style={{
                  width: 64,
                  height: 30,
                  border: '1.5px solid #0071e3',
                  borderRadius: 6,
                  padding: '0 8px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  outline: 'none',
                  textAlign: 'center',
                }}
              />
              <button
                type="button"
                className="action-btn"
                title="Save"
                onClick={commitEdit}
                style={{ color: '#0071e3' }}
              >
                ✔
              </button>
              <button type="button" className="action-btn" title="Cancel" onClick={cancelEdit}>
                ✖
              </button>
            </>
          ) : (
            <>
              <span className="li-qty">Qty: {item.qty}</span>
              <button type="button" className="action-btn" title="Edit" onClick={startEdit}>
                ✎
              </button>
              <button
                type="button"
                className="action-btn danger"
                title="Delete"
                onClick={askDelete}
              >
                🗑
              </button>
            </>
          )}
        </div>
      </div>
      <div className="devices-section">
        <div className="devices-header">
          <span className="devices-label">{placeholders.assignLabel}</span>
        </div>
        <div className="devices-chips">
          {item.devices.map((device) => (
            <span className="chip" key={device}>
              {displayDevice(device)}{' '}
              <span
                className="chip-remove"
                onClick={() =>
                  onDevicesChange(
                    item.devices.filter((entry) => entry !== device),
                    item.deviceCatalog,
                  )
                }
              >
                ×
              </span>
            </span>
          ))}
        </div>
        {item.devices.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
            {placeholders.emptyText}
          </div>
        )}
        {item.type === 'sw' ? (
          <HouseAssignmentField
            selected={item.devices}
            tree={houseTree}
            onChange={(nextSelected) => onDevicesChange(nextSelected, swCatalog)}
            isOpen={isDropdownOpen}
            onOpenChange={onDropdownOpenChange}
          />
        ) : (
          <DeviceAssignmentField
            selected={item.devices}
            catalog={item.deviceCatalog}
            onChange={onDevicesChange}
            isOpen={isDropdownOpen}
            onOpenChange={onDropdownOpenChange}
          />
        )}
      </div>
      {confirmDelete && (
        <div
          className="li-confirm-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 10,
            padding: '10px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <span style={{ color: '#991b1b' }}>
            Remove this line item? Assigned IDs will be unassigned.
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-ghost"
              style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={dismissDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{
                padding: '4px 12px',
                fontSize: 12,
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontWeight: 500,
              }}
              onClick={() => {
                onRemove()
                setConfirmDelete(false)
              }}
            >
              Remove
            </button>
          </span>
        </div>
      )}
    </div>
  )
}
